import { LoggingLevel } from '@augloop/interfaces';
import { Log, LogCategory, Operation as LoggingOperation, getNumLoggers } from '@augloop/logging';
import { AuthTokenType } from '@augloop/schemas';
import { createOperationLoggingAggregator, TelemetryLogger, scrubEuiiInUrl, setLoggableUrls } from './telemetry-logger';
import { WebSocketWorker } from './network/websocket-worker';
import { NetworkSessionManager } from './network/network-session-manager';
import { SessionProxy } from './session-proxy';
import { Utils } from './utils';
import { SessionStats } from './network/isession-manager';
import { SessionInitializer } from './network/session-initializer';
import { TokenRefreshManager } from './network/token-refresh-manager';
import { LocalWorkflowExecutionManager } from './local-workflow-execution-manager';
import { RoutingAnnotationResultsProcessor } from './annotation-processing/routing-annotation-results-processor';
import { EmptySessionManager } from './network/empty-session-manager';
import { NetworkMode } from '@augloop/session-protocol-internal';
import { v4 as uuid } from '@augloop/utils';
import { parseFlights, removeDuplicateFlights } from '@augloop/workflow-flights';
import { HttpWorker } from './network/http-worker';
const defaultAggregatorIntervalSec = 30;
export const getVersion = () => {
    /* eslint-disable-next-line @typescript-eslint/no-require-imports, node/no-missing-require */
    return require('./version.json').version;
};
export class ClientRuntime {
    constructor(workerFactory, sessionManagerFactory, sessionFactory) {
        this.sessionsByDocSessionId = new Map();
        this.isDeltaGeneratorEnabled = false;
        this.disableSyncDeltaSending = false;
        this.hasBeenInitialized = false;
        this.settings = {
            annotationsOrderingEnabled: true,
            deltaOperationsEnabled: false,
            checkIdbByFeatures: false,
            defaultBatchingEnabled: true,
            batchingWith20msIntervalEnabled: true,
            onAnnotationsSubmittedEnabled: false,
            httpFallbackEnabled: false,
            reduceBatchOperationsEnabled: false,
            batchMessagesEnabled: false,
            dynamicRateControllerEnabled: false,
            // TODO (8828820): Remove
            useRequestAuthToken: true,
            removeDuplicateFlights: true, // if true, remove duplicate flight value of the same name in SessionInitMessage
        };
        this.isDownloaderCompatible = () => {
            if (this.settings.checkIdbByFeatures) {
                if (typeof Array !== 'undefined' && typeof Array.from !== 'undefined' && typeof URL !== 'undefined' && typeof URL.createObjectURL !== 'undefined') {
                    return true;
                }
                return false;
            }
            else {
                if (typeof navigator != 'undefined' && navigator.userAgent) {
                    const ua = navigator.userAgent;
                    if (!ua.indexOf || ua.indexOf('MSIE ') > 0 || ua.indexOf('Trident/') > 0) {
                        // IE browser
                        return false;
                    }
                    else {
                        return true;
                    }
                }
                else {
                    // No userAgent
                    return false;
                }
            }
        };
        this.workerFactory = workerFactory || ((networkMode) => {
            if (networkMode && networkMode === NetworkMode.HttpFallback) {
                return new HttpWorker();
            }
            else {
                return new WebSocketWorker();
            }
        });
        const defaultSessionManagerFactory = this.createDefaultSessionManagerFactory();
        this.sessionManagerFactory = (...args) => {
            // Use the default if no factory was provided, or the provided one decides not to return an instance
            return (sessionManagerFactory === null || sessionManagerFactory === void 0 ? void 0 : sessionManagerFactory(...args)) || defaultSessionManagerFactory(...args);
        };
        this.sessionFactory = sessionFactory || ((config) => new SessionProxy(config));
    }
    /**
     * Initialize the Runtime.
     * This will succeed, even if we're unable to reach the AugLoop servers,
     * leaving the service in a local-only state.
     */
    init(serviceUrl, clientMetadata, hostCallbacks, options) {
        var _a, _b, _c;
        var _d, _e, _f, _g, _h, _j, _k;
        // dim0 = batchOptions; dim1 = hasBeenInit; dim2 = num loggers; dim3 = stack trace
        const op = new LoggingOperation({
            operationName: 'InitRuntime',
            resourceId: scrubEuiiInUrl(serviceUrl),
            dimension1: this.hasBeenInitialized.toString()
        });
        op.start();
        this.hasBeenInitialized = true;
        this.hostCallbacks = hostCallbacks;
        this.clientMetadata = clientMetadata;
        if (this.clientMetadata) {
            this.clientMetadata.runtimeVersion = getVersion();
        }
        if (this.shouldAddLogger(op)) {
            // TODO #7689155: [TS] Support for multiple runtime instances
            Log.addLogger(new TelemetryLogger(this.hostCallbacks));
            // Only add log aggregators the first time init is called
            this.addLoggingAggregator(options);
        }
        else {
            // use most recent host callbacks for logger
            Log.clearLoggers();
            Log.addLogger(new TelemetryLogger(this.hostCallbacks));
        }
        ClientRuntime.haveCalledInit = true;
        this.annotationResultsProcessor = new RoutingAnnotationResultsProcessor(() => this.settings.annotationsOrderingEnabled);
        this.inferenceServiceFactory = options.inferenceServiceFactory;
        op.dimension2 = getNumLoggers(LoggingLevel.info).toString();
        const promises = [];
        if (serviceUrl) {
            this.defaultServiceUrl = Utils.convertServiceUrlToWebSocket(serviceUrl);
            this.serviceProtocol = this.defaultServiceUrl.split(':')[0].toLowerCase();
        }
        promises.push(this.isFeatureEnabled('CheckIdbByFeatures', false).then((isEnabled) => {
            this.settings.checkIdbByFeatures = isEnabled;
        }));
        promises.push(this.isFeatureEnabled('AnnotationsOrderingEnabled', true).then((isEnabled) => {
            this.settings.annotationsOrderingEnabled = isEnabled;
        }));
        promises.push(this.isFeatureEnabled('DefaultBatchingDisabled').then((isDisabled) => {
            var _a;
            (_a = this.settings).defaultBatchingEnabled && (_a.defaultBatchingEnabled = !isDisabled);
        }));
        promises.push(this.isFeatureEnabled('HttpFallbackEnabled').then((isEnabled) => {
            var _a;
            (_a = this.settings).httpFallbackEnabled || (_a.httpFallbackEnabled = isEnabled);
        }));
        promises.push(this.isFeatureEnabled('BatchingWith20msIntervalDisabled').then((isDisabled) => {
            var _a;
            (_a = this.settings).batchingWith20msIntervalEnabled && (_a.batchingWith20msIntervalEnabled = !isDisabled);
        }));
        promises.push(this.isFeatureEnabled('OnAnnotationsSubmittedDisabled').then((isDisabled) => {
            var _a;
            (_a = this.settings).onAnnotationsSubmittedEnabled && (_a.onAnnotationsSubmittedEnabled = !isDisabled);
        }));
        promises.push(this.isFeatureEnabled('ReduceBatchOperationsEnabled').then((isEnabled) => {
            var _a;
            (_a = this.settings).reduceBatchOperationsEnabled || (_a.reduceBatchOperationsEnabled = isEnabled);
        }));
        promises.push(this.isFeatureEnabled('BatchMessagesEnabled').then((isEnabled) => {
            var _a;
            (_a = this.settings).batchMessagesEnabled || (_a.batchMessagesEnabled = isEnabled);
        }));
        promises.push(this.isFeatureEnabled('DynamicRateControllerEnabled').then((isEnabled) => {
            this.settings.dynamicRateControllerEnabled = isEnabled;
        }));
        const optionsLWEM = { enableDeltas: false, enableEarlyJoin: false };
        promises.push(Promise.all([
            this.isFeatureEnabled('DeltaOperationsEnabled').then((isEnabled) => optionsLWEM.enableDeltas = isEnabled).catch(() => optionsLWEM.enableDeltas = false),
            this.isFeatureEnabled('EarlyJoinCompletionEnabled').then((isEnabled) => optionsLWEM.enableEarlyJoin = isEnabled).catch(() => optionsLWEM.enableEarlyJoin = false)
        ]).then(() => {
            this.localWorkflowManager = new LocalWorkflowExecutionManager(options.modelDownloader && this.isDownloaderCompatible() ? options.modelDownloader : undefined, this.inferenceServiceFactory, optionsLWEM);
        }));
        // WAC flighting
        const flightsChecker = parseFlights((_a = clientMetadata.flights) !== null && _a !== void 0 ? _a : '');
        this.isDeltaGeneratorEnabled = flightsChecker.getBooleanValue('Microsoft.Office.WordOnline.AugloopDeltas', (_b = options.isDeltaGeneratorEnabled) !== null && _b !== void 0 ? _b : false);
        this.disableSyncDeltaSending = flightsChecker.getBooleanValue('Microsoft.Office.WordOnline.DisableSyncDeltaSending', (_c = options.disableSyncDeltaSending) !== null && _c !== void 0 ? _c : false);
        this.syncDeltaTimeout = flightsChecker.getIntValue('Microsoft.Office.WordOnline.SyncDeltaTimeout', options.syncDeltaTimeout);
        (_d = this.settings).httpFallbackEnabled || (_d.httpFallbackEnabled = flightsChecker.getBooleanValue('Microsoft.Office.WordOnline.Augloop.HttpFallbackEnabled', false));
        (_e = this.settings).defaultBatchingEnabled && (_e.defaultBatchingEnabled = !flightsChecker.getBooleanValue('DefaultBatchingDisabled', false));
        (_f = this.settings).batchingWith20msIntervalEnabled && (_f.batchingWith20msIntervalEnabled = !flightsChecker.getBooleanValue('BatchingWith20msIntervalDisabled', false));
        (_g = this.settings).reduceBatchOperationsEnabled || (_g.reduceBatchOperationsEnabled = flightsChecker.getBooleanValue('ReduceBatchOperationsEnabled', false));
        (_h = this.settings).batchMessagesEnabled || (_h.batchMessagesEnabled = flightsChecker.getBooleanValue('BatchMessagesEnabled', false));
        // TODO (8828820): Remove
        (_j = this.settings).useRequestAuthToken && (_j.useRequestAuthToken = flightsChecker.getBooleanValue('UseRequestAuthToken', true));
        (_k = this.settings).removeDuplicateFlights && (_k.removeDuplicateFlights = flightsChecker.getBooleanValue('RemoveDuplicateFlights', true));
        op.setDataField('Flights', JSON.stringify(this.settings));
        if (options && options.networkMode) {
            if (options.networkMode === NetworkMode.HttpFallback) {
                options.networkMode = this.settings.httpFallbackEnabled ? NetworkMode.HttpFallback : NetworkMode.JSWebSockets;
            }
            this.networkMode = options.networkMode;
            if (options.networkMode === NetworkMode.LocalWorkflowsOnly) {
                // No connections to AL service
                this.sessionManagerFactory = () => new EmptySessionManager();
            }
        }
        if (options && options.loggableUrls) {
            setLoggableUrls(options.loggableUrls);
        }
        return Promise.all(promises)
            .then(() => {
            this.batchOptions = options.batchOptions;
            if (!this.batchOptions && this.settings.defaultBatchingEnabled) {
                this.batchOptions = { delayMs: 1, maxInputSize: 1000000, delayMsMax: 50 };
                if (this.settings.batchingWith20msIntervalEnabled) {
                    this.batchOptions.delayMs = 20;
                }
            }
            if (this.batchOptions && !this.batchOptions.delayMsMax) {
                this.batchOptions.delayMsMax = 50;
            }
            op.dimension0 = JSON.stringify(this.batchOptions);
            this.logOperation(op, true);
        })
            .catch((error) => {
            this.logOperation(op, false, 'Error', error ? error.message : '(no error)');
        });
    }
    getServiceProtocol() {
        return this.serviceProtocol;
    }
    registerLocalWorkflow(workflow) {
        this.localWorkflowManager.registerLocalWorkflow(workflow);
    }
    flushTelemetry(stop) {
        Log.flushAggregators(stop);
    }
    createSession(options) {
        var _a;
        const docSessionId = options && options.docSessionId ? options.docSessionId : uuid();
        const documentId = options && options.documentId ? options.documentId : undefined;
        const existingSession = this.sessionsByDocSessionId.get(docSessionId);
        // LogOperation
        const op = new LoggingOperation({
            operationName: 'CreateSession',
            resourceId: docSessionId,
            dimension1: this.hasBeenInitialized.toString()
        });
        op.start();
        if (existingSession && existingSession.isClosed === false) {
            this.logOperation(op, false, 'Error', 'docSessionId already exists');
            throw new Error('docSessionId already exists');
        }
        let serviceUrl = options ? ((_a = options.serviceUrl) !== null && _a !== void 0 ? _a : this.defaultServiceUrl) : this.defaultServiceUrl;
        serviceUrl = Utils.convertServiceUrlToWebSocket(serviceUrl);
        if (!serviceUrl) {
            // Still allow local workflows to run for this session
            options = options || {};
            options.networkMode = NetworkMode.LocalWorkflowsOnly;
        }
        if (this.networkMode && !(options && options.networkMode)) {
            // If there is no networkMode set in options, use the one set in RuntimeInit if it exists
            options = options || {};
            options.networkMode = this.networkMode;
        }
        if (options && options.networkMode === NetworkMode.HttpFallback) {
            // Ensure flight is enabled for httpFallback
            options.networkMode = this.settings.httpFallbackEnabled ? NetworkMode.HttpFallback : NetworkMode.JSWebSockets;
        }
        // Every session gets its own customized copy of the metadata
        const clientMetadata = Object.assign({}, this.clientMetadata);
        clientMetadata.docSessionId = docSessionId;
        if (documentId) {
            clientMetadata.documentId = documentId;
        }
        if (options && options.flights) {
            clientMetadata.flights = clientMetadata.flights
                ? clientMetadata.flights + ';' + options.flights
                : options.flights;
        }
        if (this.settings.removeDuplicateFlights && clientMetadata.flights) {
            clientMetadata.flights = removeDuplicateFlights(clientMetadata.flights);
        }
        op.setDataField('Flights', clientMetadata.flights || '');
        const session = this.sessionFactory({
            hostCallbacks: this.hostCallbacks,
            sessionManager: this.sessionManagerFactory(clientMetadata, serviceUrl, options),
            batchOptions: this.batchOptions,
            extensionConfigs: (options === null || options === void 0 ? void 0 : options.extensionConfigs) || [],
            clientMetadata: clientMetadata,
            userContext: options ? options.userContext : undefined,
            localWorkflowManager: options && options.enableComplexLocalWorkflows ? undefined : this.localWorkflowManager,
            annotationResultsProcessor: this.annotationResultsProcessor,
            localRegisteredWorkflows: (options === null || options === void 0 ? void 0 : options.localRegisteredWorkflows) || [],
            enableRemoteExecutionNotification: (options === null || options === void 0 ? void 0 : options.enableRemoteExecutionNotification) || false,
            networkMode: options === null || options === void 0 ? void 0 : options.networkMode,
            egress: options ? options.egress : undefined,
            isDeltaGeneratorEnabled: this.isDeltaGeneratorEnabled,
            onAnnotationsSubmittedEnabled: this.settings.onAnnotationsSubmittedEnabled,
            disableSyncDeltaSending: this.disableSyncDeltaSending,
            syncDeltaTimeout: this.syncDeltaTimeout,
            reduceBatchOperationsEnabled: this.settings.reduceBatchOperationsEnabled,
            batchMessagesEnabled: this.settings.batchMessagesEnabled
        });
        this.sessionsByDocSessionId.set(docSessionId, session);
        if (options && options.onSessionConnect) {
            session.setConnectCallback(options.onSessionConnect);
        }
        if (options && options.onSessionDisconnect) {
            session.setDisconnectCallback(options.onSessionDisconnect);
        }
        if (options && options.onSessionReconnect) {
            session.setReconnectCallback(options.onSessionReconnect);
        }
        if (options && options.onSessionClose) {
            session.setSessionCloseCallback(options.onSessionClose);
        }
        if (options && options.onServerAuthenticationStateChangeCallback) {
            session.setServerAuthenticationStateChangeCallback(options.onServerAuthenticationStateChangeCallback);
        }
        if (options && options.onClaimsChallengeCallback) {
            session.setClaimsChallengeCallback(options.onClaimsChallengeCallback);
        }
        this.logOperation(op, true);
        return session.initialize ? session.initialize() : Promise.resolve(session);
    }
    getSessionManagerFactory() {
        return this.sessionManagerFactory;
    }
    shouldAddLogger(op) {
        if (ClientRuntime.haveCalledInit) {
            const err = new Error('Runtime already initialized');
            op.dimension3 = err.stack;
            op.dimension2 = getNumLoggers(LoggingLevel.info).toString();
            this.logOperation(op, false, 'Error', err.message);
            return false;
        }
        else {
            return true;
        }
    }
    createDefaultSessionManagerFactory() {
        return (clientMetadata, sessionServiceUrl, options) => {
            if (options && options.enableComplexLocalWorkflows) {
                // This is only available in the Phase3 bundle
                throw new Error('NYI');
            }
            if (options && options.networkMode == NetworkMode.LocalWorkflowsOnly) {
                // No connections made to AL server, allow local workflows to run
                return new EmptySessionManager();
            }
            const tokenRefreshManager = new TokenRefreshManager();
            const sessionStats = new SessionStats();
            let sendMessage = () => { Log.error(0x222c318f /* tag_8ldgp */, LogCategory.CoreDefault, 'Unexpectedly not set sendMessage'); };
            const sessionInitializer = new SessionInitializer(() => sessionStats, clientMetadata, tokenRefreshManager, (message, onResponse, sendOnlyIfConnected) => { sendMessage(message, onResponse, sendOnlyIfConnected); }, {
                // TODO (8828820): Remove `getAuthToken` and use `requestAuthToken` instead
                getAuthToken: this.settings.useRequestAuthToken ? undefined : this.getAuthToken.bind(this, clientMetadata.docSessionId),
                requestAuthToken: this.settings.useRequestAuthToken ? this.hostCallbacks.requestAuthToken : undefined,
                overrideSessionInitMessage: this.hostCallbacks.overrideSessionInitMessage,
                enableRemoteExecutionNotification: (options === null || options === void 0 ? void 0 : options.enableRemoteExecutionNotification) || false
            });
            const sessionManager = new NetworkSessionManager(Utils.convertServiceUrlToWebSocket(sessionServiceUrl), sessionStats, this.workerFactory, sessionInitializer, clientMetadata, tokenRefreshManager, this.settings, options === null || options === void 0 ? void 0 : options.networkMode);
            sendMessage = sessionManager.sendMessage.bind(sessionManager);
            sessionManager.on('disconnect', () => tokenRefreshManager.clearRefreshTimeouts());
            sessionManager.on('connect', (isSeedingRequired, sessionUrl, origin, authToken) => {
                if (isSeedingRequired && this.hostCallbacks.setSessionData) {
                    this.hostCallbacks.setSessionData(sessionUrl, origin, authToken);
                }
            });
            return sessionManager;
        };
    }
    /**
     * @deprecated TODO (8828820): Remove `getAuthToken`
     * @param docSessionId doc session id
     * @returns auth token
     */
    getAuthToken(docSessionId) {
        const tokenRequest = {
            Tickets: [],
            TokenType: AuthTokenType.Augloop,
        };
        if (docSessionId) {
            tokenRequest.DocSessionId = docSessionId;
        }
        return this.hostCallbacks.requestAuthToken(tokenRequest)
            .then((tokenResponse) => {
            if (!tokenResponse) {
                return undefined;
            }
            // In case we got the token from the host, extract its value
            return tokenResponse.Token;
        })
            .catch(() => {
            return undefined;
        });
    }
    isFeatureEnabled(featureName, defaultValue = false, scope = 'None') {
        return Utils.isFeatureEnabled(this.hostCallbacks, featureName, defaultValue, scope);
    }
    logOperation(op, success, resultSignature, resultDescription) {
        op.stop();
        op.success = success;
        op.resultSignature = resultSignature;
        op.resultDescription = resultDescription;
        Log.info(0x222c3196 /* tag_8ldgw */, LogCategory.CoreDefault, op);
    }
    addLoggingAggregator(options) {
        var _a, _b, _c, _d;
        // We need complete logs for investigating issues from non-Production rings like Microsoft. Only aggregate logs in Production ring.
        if (Utils.isChangeGateEnabled(this.hostCallbacks, 'AggregateInProductionOnly') && this.clientMetadata.releaseAudienceGroup !== 'Production') {
            return;
        }
        Log.addAggregator(createOperationLoggingAggregator('Operation', 'operationName', [
            'ExecuteBatch',
            'ReduceBatchOperations',
            'ProcessResponse',
            'LocalDeltaUpdate',
            'ApplyFormattedTextTileDeltaForLocalWorkflows',
            'ApplyTextTileDeltaForLocalWorkflows',
            'FindRangeForDelta',
            'RunModelForInferencing',
            'GetResource',
            'CreateTextTileDeltaFromItem',
            'NetworkEgressControl',
            'HttpEgress',
            'LongPollNoOp',
            'NetworkRateControllerAbandonedSyncMessage',
            'NetworkRateControllerEgress',
            'NetworkRateControllerQueueItem',
            'NetworkRateControllerOnRateLimitResponse',
            'NetworkRateControllerOnRateLimitError',
            'NetworkRateControllerRateLimitsSet',
            'NetworkRateControllerQueueItem'
        ], (_a = options.telemetryAggregationIntervalSec) !== null && _a !== void 0 ? _a : defaultAggregatorIntervalSec));
        Log.addAggregator(createOperationLoggingAggregator('Operation', 'operationName', [
            'ExecuteWorkflow',
            'ExecuteLambda',
            'EarlyJoinCompletion',
            'OnLongPollMessage',
            'OnAnnotationResultsEgress',
        ], (_b = options.telemetryAggregationIntervalSec) !== null && _b !== void 0 ? _b : 2 * defaultAggregatorIntervalSec));
        Log.addAggregator(createOperationLoggingAggregator('Operation', 'operationName', [
            'LocalScopeExecutionNotification',
            'RunLocalWorkflows',
            'EarlyJoinCompletion',
            'SetAnnotations',
            'WIS.addItemOnContextIdList',
            'WIS.setScopeItem' // from workflow-item-storage in mastermind-shared
        ], (_c = options.telemetryAggregationIntervalSec) !== null && _c !== void 0 ? _c : 4 * defaultAggregatorIntervalSec));
        Log.addAggregator(createOperationLoggingAggregator('SessionHealth', 'sessionHealthEventName', [
            'SendMessage',
            'ProcessMessage'
        ], (_d = options.telemetryAggregationIntervalSec) !== null && _d !== void 0 ? _d : 2 * defaultAggregatorIntervalSec));
    }
    uninitialize() {
        this.flushTelemetry(true);
        this.hostCallbacks = null;
        this.hasBeenInitialized = false;
        ClientRuntime.haveCalledInit = false;
        Log.clearAggregators();
        Log.clearLoggers();
    }
}
export const runtime = new ClientRuntime();
export const createRuntime = () => { return new ClientRuntime(); };
//# sourceMappingURL=runtime-client.js.map